// System
import Cookies from 'js-cookie';
import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';

// Redux
import { RouteConfig, useRoles } from '@Hooks/shared/useRoles';
import { useSelector, useDispatch } from 'react-redux';

// Actions
import {
  setUserPermissions,
  setCurrentUser,
  setPermissionedRoutes,
  setAccessToken,
  setCurrentProvider,
} from '@Modal/reducers/authorization/authorizationSlice';
import { setGame, setRgs } from '@Modal/reducers/general/generalSlice';

// Types
import { AppDispatch } from 'src/app/store';
import { StoreInterface } from 'src/app/types';
import {
  useGetSessionLazyQuery,
  useLogoutMutation,
  useRefreshTokenMutation,
  useSignInMutation,
} from '@Src/generated.graphql';
import { useApolloClient } from '@apollo/client';

export const useAuthorization = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const client = useApolloClient();
  const [signIn] = useSignInMutation();
  const [getSessionLazyQuery] = useGetSessionLazyQuery();
  const [handleRefreshToken] = useRefreshTokenMutation();
  const [logout] = useLogoutMutation();

  const { allRoutes } = useRoles();
  const { currentUser, userPermissions, permissionedRoutes, currentProvider } = useSelector(
    (state: StoreInterface) => state?.authorization
  );
  const { rgs, game } = useSelector((state: StoreInterface) => state?.general);

  const { enqueueSnackbar } = useSnackbar();

  const handleLogInReq = async (email: string, password: string) => {
    try {
      // Sign In
      const response = await signIn({ variables: { email, password } });
      Cookies.set('accessToken', response?.data?.signIn?.accessToken || '');
      Cookies.set('refreshToken', response?.data?.signIn?.refreshToken || '');
      // Get User Session
      const sessionData = await getSessionLazyQuery();
      console.log(sessionData);
      const sessionRgs = sessionData?.data?.session?.session?.rgsIds?.[0] ?? 'EU';
      const sessionGame = sessionData?.data?.session?.provider?.games?.[0] ?? 2;
      // Convert rgsIds from enum keys to enum values
      const sessionUser = sessionData?.data?.session?.session;
      const sessionProvider = sessionData?.data?.session?.provider;
      dispatch(setCurrentUser(sessionUser));
      dispatch(setCurrentProvider(sessionProvider));
      dispatch(setUserPermissions(sessionData?.data?.session?.session?.permissions));
      dispatch(setCurrentProvider(sessionData?.data?.session?.provider));
      dispatch(setRgs(sessionRgs));
      dispatch(setGame(sessionGame));
      const permissionedRoutes = currentUser?.isSuper
        ? allRoutes
        : allRoutes
            ?.filter((route: RouteConfig) =>
              route.routePermission?.some((permission) =>
                sessionData?.data?.session?.session?.permissions?.includes(permission)
              )
            )
            .map((route) => ({
              path: route.path,
              title: route.title,
              routePermission: route.routePermission,
            }));
      dispatch(setPermissionedRoutes(permissionedRoutes));
      permissionedRoutes?.length && navigate(permissionedRoutes[0]?.path);

      dispatch(setPermissionedRoutes(permissionedRoutes));
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  const fetchSessionData = async () => {
    const refreshToken = Cookies.get('refreshToken');
    try {
      const sessionData = await getSessionLazyQuery();
      const sessionRgs = sessionData?.data?.session?.session?.rgsIds?.[0] ?? 'EU';
      const sessionGame = sessionData?.data?.session?.provider?.games?.[0] ?? 2;
      // Convert rgsIds from enum keys to enum values
      const sessionUser = sessionData?.data?.session?.session;
      const sessionProvider = sessionData?.data?.session?.provider;
      dispatch(setCurrentUser(sessionUser));
      dispatch(setCurrentProvider(sessionProvider));
      dispatch(setUserPermissions(sessionData?.data?.session?.session?.permissions));
      dispatch(setCurrentProvider(sessionData?.data?.session?.provider));
      // Only set RGS if it's not already set (preserve user's selection)
      if (!rgs) {
        dispatch(setRgs(sessionRgs));
      }
      if (!game) {
        dispatch(setGame(sessionGame));
      }

      const permissionedRoutes = currentUser?.isSuper
        ? allRoutes
        : allRoutes
            ?.filter((route: RouteConfig) =>
              route.routePermission?.some((permission) =>
                sessionData?.data?.session?.session?.permissions?.includes(permission)
              )
            )
            .map((route) => ({
              path: route.path,
              title: route.title,
              routePermission: route.routePermission,
            }));
      dispatch(setPermissionedRoutes(permissionedRoutes));
    } catch (error) {
      const response = await handleRefreshToken({
        variables: {
          // @ts-ignore
          token: refreshToken,
        },
      });
      const newAccessToken = response?.data?.refreshToken?.accessToken;
      const newRefreshToken = response?.data?.refreshToken?.refreshToken;
      newAccessToken && Cookies?.set('accessToken', newAccessToken);
      newRefreshToken && Cookies?.set('refreshToken', newRefreshToken);
    }
  };

  useEffect(() => {
    fetchSessionData();
  }, []);

  const handleAccountLogout = async () => {
    await logout();
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');
    await client.clearStore();
    navigate('/');
    client.clearStore();
    window.location.reload();
  };

  return {
    // store
    allRoutes,
    currentUser,
    userPermissions,
    permissionedRoutes,
    currentProvider,

    // actions
    setAccessToken,
    setUserPermissions,
    setCurrentProvider,

    // handlers
    handleLogInReq,
    handleAccountLogout,
  };
};
